import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useMutation } from '@tanstack/react-query';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';

interface PdfGenerationResult {
  pdfUrl: string;
  filename?: string;
}

function downloadPdf(s3Url: string, name: string) {
  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${name}.pdf`);
    });
}

async function generatePdfApi(html: string): Promise<PdfGenerationResult> {
  const requestURL = `${import.meta.env.VITE_API_URL}/html-to-pdf`;
  const response = await request(requestURL, {
    method: 'POST',
    body: { html },
  });

  return {
    pdfUrl: response.pdfUrl,
    filename: response.filename,
  };
}

export function useGeneratePdfFromHtml() {
  const { t } = useTranslation(['common']);
  const { notifyError } = useNotification();

  const mutation = useMutation({
    mutationFn: generatePdfApi,
    onError: () => {
      notifyError({ title: t('common:messages.genericError') });
    },
  });

  const generate = async ({ html }: { html: string }) => {
    const result = await mutation.mutateAsync(html);
    return {
      pdfUrl: result.pdfUrl,
      filename: result.filename,
    };
  };

  const download = () => {
    if (mutation.data?.pdfUrl) {
      const name =
        mutation.data.filename ||
        `selection-${new Date().toISOString().split('T')[0]}`;
      downloadPdf(mutation.data.pdfUrl, name);
    }
  };

  return {
    generate,
    download,
    reset: mutation.reset,
    isGenerating: mutation.isPending,
    pdfUrl: mutation.data?.pdfUrl || null,
    filename: mutation.data?.filename || null,
  };
}
